/**
 * PCM WAV writing, parsing and KT1 validation (FR-71, TECH-04).
 *
 * Ported unchanged from the KT1 spike (`spikes/kt1/supabase/functions/_shared/
 * wav.ts`). The browser uses the same file (`src/platform/read-aloud/wav.ts`
 * is a byte-identical copy, checked by a test) so the client never produces
 * a file this function rejects.
 *
 * Runtime-neutral (no DOM, no Node APIs) so the same code can run in the
 * browser, in Node probe scripts and in a Deno Edge Function.
 */

export const WAV_HEADER_BYTES = 44
const BYTES_PER_SAMPLE = 2

/** KT1 target format and limits. */
export const KT1_SAMPLE_RATE = 16000
export const KT1_MAX_SECONDS = 30
export const KT1_MAX_FRAMES = KT1_SAMPLE_RATE * KT1_MAX_SECONDS
/**
 * TECH-04 "tối đa 2 MB/file" = 2,000,000 bytes (decimal MB). The KT1 bucket
 * allows 2,097,152 B; this stricter server-side check enforces the requirement.
 */
export const KT1_MAX_BYTES = 2_000_000

/** Mono 16-bit PCM WAV. Samples are clamped to [-1, 1]. */
export function encodeWavPcm16(samples: Float32Array, sampleRate: number): ArrayBuffer {
  if (!Number.isInteger(sampleRate) || sampleRate <= 0) {
    throw new RangeError(`Invalid sample rate: ${String(sampleRate)}`)
  }

  const dataBytes = samples.length * BYTES_PER_SAMPLE
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataBytes)
  const view = new DataView(buffer)

  writeAscii(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataBytes, true)
  writeAscii(view, 8, 'WAVE')
  writeAscii(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * BYTES_PER_SAMPLE, true) // byte rate
  view.setUint16(32, BYTES_PER_SAMPLE, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeAscii(view, 36, 'data')
  view.setUint32(40, dataBytes, true)

  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i] ?? 0))
    const int = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff)
    view.setInt16(WAV_HEADER_BYTES + i * BYTES_PER_SAMPLE, int, true)
  }

  return buffer
}

/** Size of a mono 16-bit WAV of the given duration. */
export function projectedWavBytes(sampleRate: number, seconds: number): number {
  return WAV_HEADER_BYTES + Math.round(sampleRate * seconds) * BYTES_PER_SAMPLE
}

export interface WavInfo {
  /** 1 = PCM integer, 3 = IEEE float (resolved through WAVE_FORMAT_EXTENSIBLE). */
  audioFormat: number
  channels: number
  sampleRate: number
  byteRate: number
  blockAlign: number
  bitsPerSample: number
  dataOffset: number
  /** Declared size of the data chunk. */
  dataBytes: number
  /** Data bytes actually present in the buffer. */
  availableDataBytes: number
  frames: number
  durationSeconds: number
}

const FORMAT_PCM = 1
const FORMAT_FLOAT = 3
const FORMAT_EXTENSIBLE = 0xfffe

/** Walks RIFF chunks (tolerates extra chunks such as LIST or FLLR). */
export function parseWav(buffer: ArrayBuffer): WavInfo {
  const view = new DataView(buffer)
  if (buffer.byteLength < 12 || readAscii(view, 0, 4) !== 'RIFF') {
    throw new Error('not a RIFF file')
  }
  if (readAscii(view, 8, 4) !== 'WAVE') throw new Error('not a WAVE file')

  let fmt: Omit<
    WavInfo,
    'dataOffset' | 'dataBytes' | 'availableDataBytes' | 'frames' | 'durationSeconds'
  > | null = null
  let offset = 12
  while (offset + 8 <= buffer.byteLength) {
    const id = readAscii(view, offset, 4)
    const size = view.getUint32(offset + 4, true)
    const body = offset + 8

    if (id === 'fmt ') {
      if (size < 16 || body + 16 > buffer.byteLength)
        throw new Error('truncated fmt chunk')
      let audioFormat = view.getUint16(body, true)
      if (audioFormat === FORMAT_EXTENSIBLE && size >= 26) {
        audioFormat = view.getUint16(body + 24, true) // first two bytes of SubFormat GUID
      }
      fmt = {
        audioFormat,
        channels: view.getUint16(body + 2, true),
        sampleRate: view.getUint32(body + 4, true),
        byteRate: view.getUint32(body + 8, true),
        blockAlign: view.getUint16(body + 12, true),
        bitsPerSample: view.getUint16(body + 14, true),
      }
    } else if (id === 'data') {
      if (fmt === null) throw new Error('data chunk before fmt chunk')
      const availableDataBytes = Math.min(size, buffer.byteLength - body)
      const frames =
        fmt.blockAlign > 0 ? Math.floor(availableDataBytes / fmt.blockAlign) : 0
      return {
        ...fmt,
        dataOffset: body,
        dataBytes: size,
        availableDataBytes,
        frames,
        durationSeconds: fmt.sampleRate > 0 ? frames / fmt.sampleRate : 0,
      }
    }
    offset = body + size + (size % 2) // chunks are word-aligned
  }
  throw new Error('no data chunk')
}

/** Decodes 16-bit PCM or 32-bit float WAV to mono float samples (channels averaged). */
export function readWavSamples(buffer: ArrayBuffer): {
  samples: Float32Array
  info: WavInfo
} {
  const info = parseWav(buffer)
  const view = new DataView(buffer)
  const { channels, frames, dataOffset, blockAlign } = info
  const samples = new Float32Array(frames)

  const isPcm16 = info.audioFormat === FORMAT_PCM && info.bitsPerSample === 16
  const isFloat32 = info.audioFormat === FORMAT_FLOAT && info.bitsPerSample === 32
  if (!isPcm16 && !isFloat32) {
    throw new Error(
      `unsupported encoding: format ${String(info.audioFormat)}, ${String(info.bitsPerSample)} bit`,
    )
  }
  const width = info.bitsPerSample / 8

  for (let f = 0; f < frames; f++) {
    let sum = 0
    for (let c = 0; c < channels; c++) {
      const at = dataOffset + f * blockAlign + c * width
      sum += isPcm16 ? view.getInt16(at, true) / 0x8000 : view.getFloat32(at, true)
    }
    samples[f] = sum / channels
  }
  return { samples, info }
}

export interface Kt1WavCheck {
  ok: boolean
  violations: string[]
  info: WavInfo | null
}

/**
 * Strict KT1 acceptance check for an uploaded or generated file: PCM 16-bit,
 * mono, 16 kHz, 1 frame to 30.000 s, at most 2,000,000 bytes, not truncated.
 */
export function validateKt1Wav(buffer: ArrayBuffer): Kt1WavCheck {
  const violations: string[] = []
  if (buffer.byteLength > KT1_MAX_BYTES) {
    violations.push(
      `file is ${String(buffer.byteLength)} B, limit ${String(KT1_MAX_BYTES)} B`,
    )
  }

  let info: WavInfo
  try {
    info = parseWav(buffer)
  } catch (e) {
    violations.push(`unreadable WAV: ${e instanceof Error ? e.message : String(e)}`)
    return { ok: false, violations, info: null }
  }

  if (info.audioFormat !== FORMAT_PCM)
    violations.push(`format ${String(info.audioFormat)}, expected PCM (1)`)
  if (info.channels !== 1)
    violations.push(`${String(info.channels)} channels, expected mono`)
  if (info.sampleRate !== KT1_SAMPLE_RATE) {
    violations.push(
      `${String(info.sampleRate)} Hz, expected ${String(KT1_SAMPLE_RATE)} Hz`,
    )
  }
  if (info.bitsPerSample !== 16)
    violations.push(`${String(info.bitsPerSample)} bit, expected 16 bit`)
  if (info.blockAlign !== info.channels * (info.bitsPerSample / 8)) {
    violations.push('inconsistent block align')
  }
  if (info.byteRate !== info.sampleRate * info.blockAlign)
    violations.push('inconsistent byte rate')
  if (info.availableDataBytes < info.dataBytes) {
    violations.push(
      `truncated: ${String(info.availableDataBytes)} of ${String(info.dataBytes)} data bytes`,
    )
  }
  if (info.frames === 0) violations.push('no audio frames')
  if (info.sampleRate === KT1_SAMPLE_RATE && info.frames > KT1_MAX_FRAMES) {
    violations.push(
      `${info.durationSeconds.toFixed(3)} s, limit ${String(KT1_MAX_SECONDS)} s`,
    )
  }

  return { ok: violations.length === 0, violations, info }
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i))
  }
}

function readAscii(view: DataView, offset: number, length: number): string {
  let out = ''
  for (let i = 0; i < length; i++) out += String.fromCharCode(view.getUint8(offset + i))
  return out
}
